import React, { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';

import type { RubricDraft, RubricProjectRole } from '../../types/rubricDraft';
import {
  COMPOUND_ROLE_NAME_MESSAGE,
  createProjectRoleId,
  isCompoundRoleName,
} from '../../types/rubricDraft';

interface RubricRolesModalProps {
  open: boolean;
  draft: RubricDraft;
  onClose: () => void;
  onChange: (draft: RubricDraft) => void;
}

export function RubricRolesModal({ open, draft, onClose, onChange }: RubricRolesModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const roles = draft.projectRoles ?? [];

  const handleAdd = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (isCompoundRoleName(trimmed)) {
      setError(COMPOUND_ROLE_NAME_MESSAGE);
      return;
    }
    const role: RubricProjectRole = {
      id: createProjectRoleId(),
      name: trimmed,
      description: description.trim() || undefined,
    };
    onChange({ ...draft, projectRoles: [...roles, role] });
    setName('');
    setDescription('');
    setError(null);
  };

  const handleUpdate = (roleId: string, patch: Partial<RubricProjectRole>) => {
    if (typeof patch.name === 'string' && isCompoundRoleName(patch.name)) {
      setError(COMPOUND_ROLE_NAME_MESSAGE);
      return;
    }
    setError(null);
    onChange({
      ...draft,
      projectRoles: roles.map((role) => (role.id === roleId ? { ...role, ...patch } : role)),
    });
  };

  const handleDelete = (roleId: string) => {
    const nextRoles = roles.filter((role) => role.id !== roleId);
    const criteria = draft.criteria.map((criterion) => {
      const ids = criterion.applicableRoleIds ?? ['*'];
      if (ids.includes('*')) return criterion;
      const filtered = ids.filter((id) => id !== roleId);
      return {
        ...criterion,
        applicableRoleIds: filtered.length ? filtered : ['*'],
      };
    });
    onChange({ ...draft, projectRoles: nextRoles, criteria });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rubric-roles-title"
      onClick={onClose}
    >
      <div
        className="max-h-[min(90dvh,36rem)] w-full max-w-lg overflow-hidden rounded-2xl border shadow-xl"
        style={{
          borderColor: 'var(--sala-border-strong)',
          background: 'var(--sala-surface-raised)',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="flex items-center justify-between border-b px-5 py-4"
          style={{ borderColor: 'var(--sala-border-strong)' }}
        >
          <div>
            <h2 id="rubric-roles-title" className="font-display text-lg font-semibold tracking-tight">
              Roles del proyecto
            </h2>
            <p className="mt-1 text-xs text-[var(--sala-ink-muted)]">
              Define roles ABP (locución, sonido, análisis…). Los criterios se etiquetan después.
            </p>
          </div>
          <button type="button" className="sala-btn-outline p-2" onClick={onClose} aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[18rem] space-y-2 overflow-y-auto px-5 py-4">
          {roles.length === 0 ? (
            <p
              className="rounded-xl border border-dashed px-4 py-6 text-center text-sm text-[var(--sala-ink-muted)]"
              style={{ borderColor: 'var(--sala-border-strong)' }}
            >
              Aún no hay roles. Agrega el primero abajo.
            </p>
          ) : (
            roles.map((role) => (
              <div
                key={role.id}
                className="rounded-xl border p-3"
                style={{ borderColor: 'var(--sala-border-strong)' }}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1 space-y-2">
                    <input
                      className="sala-input-dark w-full text-sm font-semibold"
                      value={role.name}
                      onChange={(event) => handleUpdate(role.id, { name: event.target.value })}
                      placeholder="Nombre del rol"
                    />
                    {isCompoundRoleName(role.name) ? (
                      <p className="text-[10px] text-amber-300" role="status">
                        Este nombre combina funciones. Renómbralo a un solo rol (el guardado bloquea
                        compuestos nuevos).
                      </p>
                    ) : null}
                    <input
                      className="sala-input-dark w-full text-xs"
                      value={role.description ?? ''}
                      onChange={(event) =>
                        handleUpdate(role.id, { description: event.target.value || undefined })
                      }
                      placeholder="Descripción opcional"
                    />
                  </div>
                  <button
                    type="button"
                    className="sala-btn-outline p-2 text-red-400"
                    onClick={() => handleDelete(role.id)}
                    aria-label={`Eliminar ${role.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t px-5 py-4" style={{ borderColor: 'var(--sala-border-strong)' }}>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[var(--sala-ink-muted)]">
            Nuevo rol
          </p>
          {error ? (
            <p className="mb-2 text-[11px] text-red-400" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              className="sala-input-dark flex-1 text-sm"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (error) setError(null);
              }}
              placeholder="Ej: Análisis musical"
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleAdd();
              }}
            />
            <input
              className="sala-input-dark flex-1 text-sm"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Descripción (opcional)"
            />
            <button
              type="button"
              className="sala-btn-hero sala-btn-hero-compact rubric-roles-modal__cta"
              onClick={handleAdd}
              disabled={!name.trim()}
            >
              <Plus className="mr-1 inline h-3.5 w-3.5" />
              Agregar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
